import LocalStorageManager from '@/utils/localStorageManager';

/**
 * The Gontijo client session, as the browser holds it.
 *
 * `token` is the `client_auth` the ERP issues through platform-purchase. It is not a bearer
 * token the funnel puts in a header: the backend reads it out of the request *body* — as a
 * ticket attribute and inside the payment's `meta` — and builds the `GTJ-CLI-Authorization`
 * header itself on the way to the ERP. That is the whole reason this value has to live
 * somewhere JavaScript can read, which is what rules out the one cookie worth having.
 */
interface GontijoSessionRecord {
  token: string;
  /** Epoch ms, or null when the ERP's `expires` could not be read. See {@link parseGontijoExpires}. */
  expiresAt: number | null;
  issuedAt: number;
  /**
   * The address the buyer signed in with, as the login widget announced it. Kept beside the token
   * because it is only ever told once, at sign-in, and the passengers form offers it long after —
   * possibly after a reload or the 3D Secure round trip. Absent for sessions stored before it was.
   */
  email?: string;
}

/** Identifies the session's owner and the backend that issued it. */
interface GontijoSessionScope {
  /** `whitelabelConfig.env.brand`. */
  brand: string;
  /** `whitelabelConfig.env.api.purchaseUrl`. */
  purchaseUrl: string;
}

/**
 * How long before its stated expiry a session starts reading as expired.
 *
 * Not decoration: without it a payment launched a second before the deadline is answered after
 * it, and the buyer gets a 401 in the middle of the one operation that must not fail. A minute
 * covers the round trip with room to spare, and is short enough not to cost a usable session.
 */
const EXPIRY_BUFFER_MS = 60_000;

/**
 * Brazil's offset from UTC, as fixed minutes.
 *
 * The ERP stamps `expires` with no zone at all, and it is a Brazilian carrier answering in local
 * time. Brazil abolished DST in 2019, so a constant is correct rather than merely convenient — but
 * it is a constant about *their* clock, which is why it is named and not inlined as `3`.
 */
const BRT_OFFSET_MINUTES = -180;

const STORAGE_KEY_PREFIX = 'gontijo.session.v1';

/**
 * Sessions that could not be written, kept for the life of the tab.
 *
 * Safari in private mode throws on `setItem` rather than failing quietly, and a browser with site
 * data blocked reads back empty. Neither should cost the buyer their session mid-purchase: the
 * in-memory copy is worse than storage — it does not survive the 3D Secure round trip — but it is
 * far better than being signed out on the payment screen for a reason nobody can see.
 */
const memoryFallback = new Map<string, GontijoSessionRecord>();

/**
 * Keys whose stored copy is known to be out of step with the truth.
 *
 * `LocalStorageManager.set` and `delete` *return* their error rather than throwing it, so a failed
 * write or a failed removal looks exactly like a successful one to the caller. Both leave whatever
 * was already in storage sitting there, and reads prefer storage over memory — which means a quota
 * error during sign-in would hand back the previous buyer's token, and one during sign-out would
 * restore the session that was just signed out of. Once a key is in here, storage is no longer an
 * answer for it: memory is, for the life of the tab.
 */
const staleInStorage = new Set<string>();

/** Writes through the manager, reporting whether the stored copy now matches. */
function persistRecord(key: string, record: GontijoSessionRecord): boolean {
  try {
    // A returned value is the error; `undefined` is the success. The try/catch stays for the read
    // of `localStorage` itself, which throws outright where site data is blocked.
    return !LocalStorageManager.set(key, record);
  } catch {
    return false;
  }
}

/** Removes through the manager, reporting whether the stored copy is really gone. */
function eraseRecord(key: string): boolean {
  try {
    return !LocalStorageManager.delete(key);
  } catch {
    return false;
  }
}

/**
 * The storage key for one brand on one backend.
 *
 * Both halves matter, for different reasons. The brand is the obvious one — this funnel serves many
 * and the subdomain is which. The backend is the one that is easy to miss: a token minted by
 * sandbox is not merely useless against production, it is another tenant's secret, and a developer
 * pointing a local build at a different API would otherwise inherit whatever the last one left.
 *
 * Note this is defence in depth and not the isolation mechanism. `localStorage` is scoped to the
 * origin by the browser, and the origin *is* the brand, so two brands cannot see each other's
 * storage whatever this function returns. That is precisely why the storage decision was made this
 * way: isolation that holds because of how the web works outlives isolation that holds because
 * everyone remembered to namespace their keys.
 */
function gontijoSessionStorageKey({ brand, purchaseUrl }: GontijoSessionScope): string {
  const backend = readHost(purchaseUrl);

  return `${STORAGE_KEY_PREFIX}:${(brand || 'unknown').toLowerCase()}:${backend}`;
}

/** The host of a URL, or a stable stand-in when it will not parse. */
function readHost(url: string): string {
  if (!url) return 'unknown';

  try {
    return new URL(url).host.toLowerCase();
  } catch {
    return 'unknown';
  }
}

/**
 * The ERP's expiry as epoch milliseconds, or null when it cannot be read.
 *
 * **Never hand this string to `new Date()` or `Date.parse`.** The value Gontijo actually sends —
 * captured against their live API on 2026-08-13 — is `28/08/2026 16:09:02`, and the browser's
 * parser reads that as month 28: `Invalid Date`, then `NaN`, then a session that is expired the
 * instant it arrives. A specification that says "parse the date" is how this bug ships.
 *
 * Two formats are recognised because two have been observed. Anything else answers null, which is
 * deliberately *not* the same as expired — see {@link isGontijoSessionExpired}. A provider that
 * changes its date format must not sign everybody out; `validate_token` is the authority on whether
 * a session is alive, and this is only the shortcut that saves asking.
 */
function parseGontijoExpires(raw: unknown): number | null {
  if (typeof raw !== 'string') return null;

  const value = raw.trim();
  // Rejections send the literal string "null" in this field, the same way they do for the token.
  if (!value || value.toLowerCase() === 'null') return null;

  const slashed = /^(\d{2})\/(\d{2})\/(\d{4})[ T](\d{2}):(\d{2}):(\d{2})$/.exec(value);
  if (slashed) {
    const [, day, month, year, hour, minute, second] = slashed;
    return brasiliaToEpoch(+year, +month, +day, +hour, +minute, +second);
  }

  const compact = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/.exec(value);
  if (compact) {
    const [, year, month, day, hour, minute, second] = compact;
    return brasiliaToEpoch(+year, +month, +day, +hour, +minute, +second);
  }

  return null;
}

/**
 * Wall-clock time in Brazil to epoch milliseconds.
 *
 * `Date.UTC` is used rather than the `Date` constructor on purpose: the constructor interprets its
 * arguments in *the viewer's* zone, so the same token would expire at different moments for a buyer
 * in São Paulo and one in Mexico City. Rejects impossible dates, which `Date.UTC` would otherwise
 * roll over silently — a month of 13 becoming January of the next year is not an expiry anyone meant.
 */
function brasiliaToEpoch(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
): number | null {
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59 || second > 59)
    return null;

  const utc = Date.UTC(year, month - 1, day, hour, minute, second);
  const date = new Date(utc);
  const rolled = date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day;
  if (rolled) return null;

  return utc - BRT_OFFSET_MINUTES * 60_000;
}

/**
 * Whether the session should be treated as gone. The only place that decides this.
 *
 * An unknown expiry is not an expired one. When `expiresAt` is null the ERP's answer could not be
 * read, and the honest response is to keep the session and let `validate_token` rule on it, rather
 * than to sign out a buyer whose session is very probably fine.
 */
function isGontijoSessionExpired(
  record: GontijoSessionRecord | null,
  now: number = Date.now(),
): boolean {
  if (!record?.token) return true;
  if (record.expiresAt === null) return false;

  return now >= record.expiresAt - EXPIRY_BUFFER_MS;
}

/** Builds a record from what `login_2fa` answers. */
function buildGontijoSessionRecord(
  token: string,
  expires: unknown,
  now: number = Date.now(),
): GontijoSessionRecord {
  return { token, expiresAt: parseGontijoExpires(expires), issuedAt: now };
}

/** The stored session for this brand and backend, or null. Malformed entries read as absent. */
function readGontijoSession(scope: GontijoSessionScope): GontijoSessionRecord | null {
  const key = gontijoSessionStorageKey(scope);
  const remembered = memoryFallback.get(key) ?? null;

  // Storage lost its say over this key the moment a write or a removal failed on it.
  if (staleInStorage.has(key)) return remembered;

  let stored: unknown = null;
  try {
    stored = LocalStorageManager.get(key);
  } catch {
    stored = null;
  }

  return asRecord(stored) ?? remembered;
}

/**
 * What `localStorage` itself holds for this key, or null. No memory fallback, no stale mark.
 *
 * For answering another tab's removal, where the question is what that tab left behind: a read with
 * the fallback would hand this tab's old token straight back, and a key another tab has since
 * rewritten holds a new sign-in that must be adopted rather than erased.
 */
function readPersistedGontijoSession(scope: GontijoSessionScope): GontijoSessionRecord | null {
  try {
    return asRecord(LocalStorageManager.get(gontijoSessionStorageKey(scope)));
  } catch {
    return null;
  }
}

/**
 * Drops this tab's copy of the session and leaves `localStorage` alone.
 *
 * For a removal another tab already made: the stored key is gone, so storage is truthful again and
 * the stale mark goes too. Calling `removeItem` here instead could erase a record a third tab wrote
 * after that removal.
 */
function forgetGontijoSessionLocally(scope: GontijoSessionScope): void {
  const key = gontijoSessionStorageKey(scope);

  memoryFallback.delete(key);
  staleInStorage.delete(key);
}

/** Narrows whatever came back out of storage, which is `unknown` until proven otherwise. */
function asRecord(stored: unknown): GontijoSessionRecord | null {
  if (!stored || typeof stored !== 'object') return null;

  const { token, expiresAt, issuedAt, email } = stored as Partial<GontijoSessionRecord>;
  if (typeof token !== 'string' || !token) return null;

  return {
    token,
    expiresAt: typeof expiresAt === 'number' ? expiresAt : null,
    issuedAt: typeof issuedAt === 'number' ? issuedAt : 0,
    ...(typeof email === 'string' && email && { email }),
  };
}

/** Persists the session, falling back to memory where storage will not take it. */
function writeGontijoSession(scope: GontijoSessionScope, record: GontijoSessionRecord): void {
  const key = gontijoSessionStorageKey(scope);

  memoryFallback.set(key, record);

  // The memory copy above is the fallback; a tab-lived session beats none. What it must not do is
  // lose a race with whatever the failed write left behind.
  if (persistRecord(key, record)) staleInStorage.delete(key);
  else staleInStorage.add(key);
}

/**
 * Removes every trace of the session.
 *
 * Both copies, always. Clearing only the stored one left the in-memory fallback holding a token
 * that the next read would happily hand back — a sign-out that does not sign out.
 */
function clearGontijoSession(scope: GontijoSessionScope): void {
  const key = gontijoSessionStorageKey(scope);

  memoryFallback.delete(key);

  // A removal that did not remove leaves the token readable. Marking the key is what stops the next
  // read from finding it and signing the buyer back in.
  if (eraseRecord(key)) staleInStorage.delete(key);
  else staleInStorage.add(key);
}

export {
  EXPIRY_BUFFER_MS,
  buildGontijoSessionRecord,
  clearGontijoSession,
  forgetGontijoSessionLocally,
  gontijoSessionStorageKey,
  isGontijoSessionExpired,
  parseGontijoExpires,
  readGontijoSession,
  readPersistedGontijoSession,
  writeGontijoSession,
};
export type { GontijoSessionRecord, GontijoSessionScope };
